import { createContext, useContext, useEffect, useState } from "react";
import { searchPlans } from "src/billinglatest/clients/service.client";
import { Plan } from "src/billinglatest/types";
import { formatted } from "src/billinglatest/util/currency";
import {
  ALL_CURRENCY_CODES,
  BillingCycle,
  CurrencyCode,
  PlanId,
} from "./constants";

/**
 * Onboarding plan catalog (v4 = Core/Pro). Prices come from the billing API
 * (`searchPlans`) — never hardcoded. `searchPlans` builds its client from the
 * session token, so it works in the onboarding tree without BillingContext;
 * it returns `undefined` when there is no auth token, which we treat as an error.
 */
export interface OnboardingPlans {
  plans: Plan[];
  loading: boolean;
  error: boolean;
  /**
   * Find the plan for a tier + billing cycle + currency, or undefined. Resolves
   * by the canonical plan id (`{group}-{cycle}-{currency}`, e.g.
   * `core-year-gbp`) rather than by matching the group/term/currency fields —
   * the v4 catalog can return more than one entry matching those fields (a
   * legacy £0 variant alongside the current plan), and a field match would pick
   * the wrong one (GRWBKK-1669).
   */
  getPlan: (
    group: PlanId,
    cycle: BillingCycle,
    currency: CurrencyCode,
  ) => Plan | undefined;
}

const matchesOnboarding = (plan: Plan): boolean =>
  (plan.group === "core" || plan.group === "pro") &&
  plan.termNumber === 1 &&
  (plan.term === "month" || plan.term === "year");

export const useOnboardingPlans = (): OnboardingPlans => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Fetch every currency once on mount, then filter client-side, so changing
  // currency or billing cycle never triggers another request.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(false);
      try {
        const result = await searchPlans({
          version: "v4",
          currencyCode: ALL_CURRENCY_CODES,
        });
        if (cancelled) return;
        if (!result) {
          // No billing client / no auth token.
          setError(true);
          setPlans([]);
          return;
        }
        setPlans(result.filter(matchesOnboarding));
      } catch {
        if (!cancelled) {
          setError(true);
          setPlans([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const getPlan = (
    group: PlanId,
    cycle: BillingCycle,
    currency: CurrencyCode,
  ): Plan | undefined => {
    const planId = `${group}-${cycle}-${currency.toLowerCase()}`;
    return plans.find((plan) => plan.id === planId);
  };

  return { plans, loading, error, getPlan };
};

/**
 * Per-screen monthly price for display (the design shows a monthly figure even
 * when billed yearly). Yearly plans store the annual per-license price, so we
 * divide by 12; monthly plans are already per-month. Prices are in cents.
 */
export const perScreenMonthlyPrice = (plan: Plan): string => {
  const cents = plan.term === "year" ? plan.price / 12 : plan.price;
  return formatted(plan.currency.toUpperCase(), cents);
};

/** Footer total = per-license price × license count, in the plan's period. */
export const planTotal = (plan: Plan, licenses: number): string =>
  formatted(plan.currency.toUpperCase(), plan.price * licenses);

/** Raw savings (in cents) for the free-device deal — one device per license. */
export const deviceDealSavingsCents = (
  perDeviceCents: number,
  licenses: number,
): number => perDeviceCents * licenses;

/** Context value shared between the Plan & Billing step and the footer total. */
export interface PlanBillingContextValue extends OnboardingPlans {
  /** Pro-trialist users see only Pro (Monthly/Annual) with no cycle toggle. */
  isProTrialist: boolean;
}

export const OnboardingPlansContext =
  createContext<PlanBillingContextValue | null>(null);

export const useOnboardingPlansContext = (): PlanBillingContextValue => {
  const ctx = useContext(OnboardingPlansContext);
  if (!ctx) {
    throw new Error(
      "useOnboardingPlansContext must be used within OnboardingPlansContext.Provider",
    );
  }
  return ctx;
};
